import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  FileText,
  UserCheck,
  Scale,
  AlertCircle,
  Mail,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Download,
  Printer,
  Eye,
  RefreshCw,
  Trash2,
  HelpCircle
} from "lucide-react";

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = useState("overview");

  const lastUpdated = "October 8, 2026";
  const effectiveDate = "October 8, 2026";

  const handlePrint = () => {
    window.print();
  };

  const openCookiePreferences = () => {
    window.dispatchEvent(new CustomEvent("open-cookie-preferences"));
  };

  const sections = [
    { id: "overview", label: "1. Overview & Data Fiduciary" },
    { id: "dpdp-notice", label: "2. DPDP Notice (Section 5)" },
    { id: "data-collected", label: "3. Personal Data Collected" },
    { id: "consent-basis", label: "4. Consent & Legal Basis (Sec 6)" },
    { id: "fiduciary-obligations", label: "5. Security & Safeguards (Sec 8)" },
    { id: "principal-rights", label: "6. Data Principal Rights (Sec 11-14)" },
    { id: "children-data", label: "7. Protection of Minors (Sec 9)" },
    { id: "third-parties", label: "8. Processors & Transfers (Sec 16)" },
    { id: "cookies", label: "9. Cookies & Tracking" },
    { id: "retention", label: "10. Retention & Erasure Schedule" },
    { id: "grievance", label: "11. Grievance Officer & Redressal" },
  ];

  return (
    <main className="pt-28 md:pt-36 pb-20 bg-white">
      {/* Hero Header */}
      <section className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-wrap items-center gap-2 text-[11px] tracking-[0.2em] font-semibold text-black/60 mb-3">
          <Link to="/" className="hover:text-black">HOME</Link>
          <ChevronRight className="h-3.5 w-3.5 text-black/30" />
          <span className="text-black">LEGAL</span>
          <ChevronRight className="h-3.5 w-3.5 text-black/30" />
          <span className="text-black">PRIVACY POLICY</span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-8 border-b border-black/10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black text-white text-[11px] font-bold tracking-[0.15em] mb-4">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
              DPDP ACT, 2023 COMPLIANT (INDIA)
            </div>
            <h1 className="font-orbitron font-black text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Privacy Policy &amp;
              <br />
              <span className="italic font-normal text-black/60">Data Protection Statement</span>
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-black/70 leading-relaxed">
              BUGZ X is committed to the highest standards of data security, transparency, and personal privacy.
              This document serves as our statutory notice under the <strong>Digital Personal Data Protection Act, 2023 (India)</strong>.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/15 text-xs font-semibold hover:bg-black/5 transition-colors"
            >
              <Printer className="h-3.5 w-3.5" /> Print / PDF
            </button>
            <button
              onClick={openCookiePreferences}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-black text-white text-xs font-semibold hover:bg-black/85 transition-colors"
            >
              <Lock className="h-3.5 w-3.5" /> Manage Cookie Consent
            </button>
          </div>
        </div>

        {/* Metadata summary bar */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-2xl bg-black/[0.02] border border-black/10 text-xs">
          <div>
            <p className="text-black/40 text-[10px] tracking-widest font-semibold uppercase">Jurisdiction</p>
            <p className="mt-1 font-bold">India (DPDP Act 2023)</p>
          </div>
          <div>
            <p className="text-black/40 text-[10px] tracking-widest font-semibold uppercase">Last Updated</p>
            <p className="mt-1 font-bold">{lastUpdated}</p>
          </div>
          <div>
            <p className="text-black/40 text-[10px] tracking-widest font-semibold uppercase">Effective Date</p>
            <p className="mt-1 font-bold">{effectiveDate}</p>
          </div>
          <div>
            <p className="text-black/40 text-[10px] tracking-widest font-semibold uppercase">Grievance SLA</p>
            <p className="mt-1 font-bold text-emerald-700">48h Ack / 30d Resolve</p>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar Nav */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Sidebar Nav */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 p-5 rounded-3xl border border-black/12 bg-white/90 backdrop-blur shadow-sm space-y-2">
              <p className="text-[10px] tracking-[0.25em] font-bold text-black/50 uppercase px-2 mb-3">
                TABLE OF CONTENTS
              </p>
              <nav className="space-y-1">
                {sections.map((sec) => (
                  <a
                    key={sec.id}
                    href={`#${sec.id}`}
                    onClick={() => setActiveSection(sec.id)}
                    className={`block px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                      activeSection === sec.id
                        ? "bg-black text-white"
                        : "text-black/70 hover:bg-black/5 hover:text-black"
                    }`}
                  >
                    {sec.label}
                  </a>
                ))}
              </nav>

              <div className="pt-4 mt-4 border-t border-black/10">
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-950">
                  <p className="font-bold flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-700 shrink-0" />
                    Data Protection Notice
                  </p>
                  <p className="mt-1 text-black/70 leading-relaxed">
                    By accessing our website or submitting forms, you acknowledge the terms described herein.
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* Legal Text Body */}
          <div className="lg:col-span-8 space-y-12 text-sm text-black/80 leading-relaxed">
            
            {/* Section 1 */}
            <div id="overview" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  01
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Overview &amp; Data Fiduciary Details
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  This Privacy Policy governs the manner in which <strong>BUGZ X</strong> (&ldquo;BUGZ X&rdquo;, &ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;), acting as a <strong>Data Fiduciary</strong> under the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act, 2023)</strong> and the Information Technology Act, 2000 (with applicable IT Rules), collects, processes, stores, protects, and discloses personal data collected from users, clients, candidates, and website visitors (&ldquo;Data Principals&rdquo; or &ldquo;you&rdquo;) through our website (<a href="https://bugzx.space" className="underline font-semibold">https://bugzx.space</a> and associated subdomains) and related digital services.
                </p>
                <div className="p-4 rounded-2xl bg-black/[0.03] border border-black/10">
                  <p className="font-bold text-black text-xs uppercase tracking-wider mb-2">Data Fiduciary Identity:</p>
                  <ul className="space-y-1.5 text-xs text-black/75">
                    <li><strong>Legal Entity / Brand:</strong> BUGZ X (AI Systems &amp; Digital Engineering)</li>
                    <li><strong>Country of Operation:</strong> Republic of India (Global Delivery)</li>
                    <li><strong>Official Electronic Inquiries:</strong> <a href="mailto:team@bugzx.space" className="underline">team@bugzx.space</a></li>
                    <li><strong>Designated Data Protection Contact:</strong> <a href="mailto:grievance@bugzx.space" className="underline">grievance@bugzx.space</a></li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div id="dpdp-notice" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  02
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Statutory Notice Under Section 5 (DPDP Act, 2023)
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  In accordance with <strong>Section 5 of the DPDP Act, 2023</strong>, whenever we seek your consent or collect personal data, we give you notice specifying:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-4 rounded-2xl border border-black/10 bg-black/[0.02]">
                    <p className="font-bold text-xs text-black flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      Specified Purposes
                    </p>
                    <p className="mt-1 text-xs text-black/70">
                      Processing inquiries, executing service contracts, delivering AI/software solutions, customer support, recruitment, and statutory compliance.
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl border border-black/10 bg-black/[0.02]">
                    <p className="font-bold text-xs text-black flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      Exercising Rights
                    </p>
                    <p className="mt-1 text-xs text-black/70">
                      The seamless manner in which you can access, update, rectify, or withdraw consent for your personal data.
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl border border-black/10 bg-black/[0.02]">
                    <p className="font-bold text-xs text-black flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      Grievance Redressal
                    </p>
                    <p className="mt-1 text-xs text-black/70">
                      Direct access to our designated Grievance Officer with enforceable resolution timelines.
                    </p>
                  </div>
                  <div className="p-4 rounded-2xl border border-black/10 bg-black/[0.02]">
                    <p className="font-bold text-xs text-black flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      Board Escalation
                    </p>
                    <p className="mt-1 text-xs text-black/70">
                      Your statutory right to file a complaint before the <strong>Data Protection Board of India (DPBI)</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div id="data-collected" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  03
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Categories of Personal Data Collected
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  We practice strict <strong>Data Minimization</strong>. We only collect digital personal data that is strictly necessary for fulfilling legitimate business operations and requested services.
                </p>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border border-black/10 rounded-2xl overflow-hidden">
                    <thead className="bg-black text-white">
                      <tr>
                        <th className="p-3">Data Category</th>
                        <th className="p-3">Examples of Data Collected</th>
                        <th className="p-3">Primary Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-black/10 bg-white">
                      <tr>
                        <td className="p-3 font-semibold">Direct Identifiers</td>
                        <td className="p-3">Full name, email address, mobile/telephone number, corporate organization name.</td>
                        <td className="p-3 text-black/70">Contact verification, response to service inquiries, proposal dispatch.</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold">Location &amp; Demographics</td>
                        <td className="p-3">Country, City, IP-derived geographical region.</td>
                        <td className="p-3 text-black/70">Time zone coordination, localized service delivery, regulatory compliance.</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold">Recruitment / Candidate Data</td>
                        <td className="p-3">Curriculum Vitae (CV), resume files, portfolio links, academic/employment background, cover letters.</td>
                        <td className="p-3 text-black/70">Assessing eligibility for internship and engineering openings at BUGZ X.</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold">Commercial &amp; Project Info</td>
                        <td className="p-3">Project requirements, budget expectations, company domain, technical scope.</td>
                        <td className="p-3 text-black/70">Scoping architectural solutions, commercial proposals, and SLAs.</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold">Technical &amp; Telemetry Data</td>
                        <td className="p-3">Browser user-agent, operating system, timestamp, session identifiers, referral URLs.</td>
                        <td className="p-3 text-black/70">System security, fraud mitigation, API performance optimization.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div id="consent-basis" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  04
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Consent Framework &amp; Legal Basis (Section 6)
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  Under <strong>Section 6 of the DPDP Act, 2023</strong>, consent given by the Data Principal must be <strong>free, specific, informed, unconditional, and unambiguous</strong>, expressed through a clear affirmative action.
                </p>
                
                <div className="p-4 rounded-2xl border border-black/10 bg-black/[0.02] space-y-3 text-xs">
                  <p className="font-bold text-black text-sm">Affirmative Consent Mechanisms:</p>
                  <ul className="list-disc pl-5 space-y-1.5 text-black/75">
                    <li>When submitting our contact or inquiry form, you must explicitly check the consent confirmation checkbox.</li>
                    <li>When browsing our website, our Cookie &amp; DPDP Consent Banner allows you to selectively enable or reject optional analytics cookies.</li>
                    <li>No pre-ticked checkboxes or forced bundled consents are utilized anywhere on the platform.</li>
                  </ul>
                </div>

                <div className="p-4 rounded-2xl border border-emerald-500/30 bg-emerald-50 text-emerald-950 text-xs">
                  <p className="font-bold flex items-center gap-2 text-emerald-900">
                    <RefreshCw className="h-4 w-4 text-emerald-700" />
                    Right to Withdraw Consent at Any Time:
                  </p>
                  <p className="mt-1 leading-relaxed">
                    You may withdraw your consent at any time as easily as it was given. To withdraw consent for communication or data processing, simply email <a href="mailto:grievance@bugzx.space" className="underline font-bold">grievance@bugzx.space</a> with the subject line <em>&ldquo;Withdrawal of Consent - [Your Name/Email]&rdquo;</em>. Upon verification, we will cease processing and delete your personal data within statutory timeframes, unless retention is required by law.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div id="fiduciary-obligations" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  05
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Obligations &amp; Reasonable Security Safeguards (Section 8)
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  Under <strong>Section 8 of the DPDP Act, 2023</strong>, BUGZ X implements comprehensive administrative, physical, and technical safeguards to prevent unauthorized access, loss, alteration, or personal data breach:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  <div className="p-4 rounded-2xl border border-black/10">
                    <p className="font-bold text-black flex items-center gap-2">
                      <Lock className="h-4 w-4 text-black" />
                      Encryption in Transit &amp; at Rest
                    </p>
                    <p className="mt-1.5 text-black/70">
                      All data in transit is encrypted using modern TLS 1.3 / HTTPS. Sensitive stored records and cloud storage buckets utilize AES-256 encryption.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl border border-black/10">
                    <p className="font-bold text-black flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-black" />
                      Strict Role-Based Access Controls
                    </p>
                    <p className="mt-1.5 text-black/70">
                      Only authorized team leads with multi-factor authentication (MFA) and explicit confidentiality obligations are permitted access to inquiry/candidate data.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl border border-black/10">
                    <p className="font-bold text-black flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-black" />
                      Data Breach Notification Protocol
                    </p>
                    <p className="mt-1.5 text-black/70">
                      In the unforeseen event of a personal data breach, BUGZ X will notify the <strong>Data Protection Board of India (DPBI)</strong> and affected Data Principals in the prescribed form and manner.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl border border-black/10">
                    <p className="font-bold text-black flex items-center gap-2">
                      <Trash2 className="h-4 w-4 text-black" />
                      Automated Data Erasure
                    </p>
                    <p className="mt-1.5 text-black/70">
                      We erase personal data as soon as the purpose for which it was collected is satisfied and legal retention obligations expire.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 6 */}
            <div id="principal-rights" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  06
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Your Statutory Rights as a Data Principal (Sections 11–14)
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  The DPDP Act, 2023 grants you significant and enforceable rights over your personal data. You may exercise any of the following rights without cost:
                </p>

                <div className="space-y-3 text-xs">
                  <div className="p-4 rounded-2xl border border-black/10 flex items-start gap-3">
                    <div className="h-7 w-7 rounded-full bg-black text-white flex items-center justify-center font-bold text-[11px] shrink-0 mt-0.5">
                      11
                    </div>
                    <div>
                      <p className="font-bold text-sm text-black">Right to Access Information (Section 11)</p>
                      <p className="mt-1 text-black/70">
                        You have the right to obtain a summary of personal data being processed by us, a description of the processing activities, and the identities of all third-party Data Processors with whom data has been shared.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl border border-black/10 flex items-start gap-3">
                    <div className="h-7 w-7 rounded-full bg-black text-white flex items-center justify-center font-bold text-[11px] shrink-0 mt-0.5">
                      12
                    </div>
                    <div>
                      <p className="font-bold text-sm text-black">Right to Correction &amp; Erasure (Section 12)</p>
                      <p className="mt-1 text-black/70">
                        You have the right to correct inaccurate or misleading personal data, complete incomplete personal data, update outdated personal data, and request the erasure of personal data that is no longer required for the purpose for which it was processed.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl border border-black/10 flex items-start gap-3">
                    <div className="h-7 w-7 rounded-full bg-black text-white flex items-center justify-center font-bold text-[11px] shrink-0 mt-0.5">
                      13
                    </div>
                    <div>
                      <p className="font-bold text-sm text-black">Right of Grievance Redressal (Section 13)</p>
                      <p className="mt-1 text-black/70">
                        You have the right to register a grievance with our designated Grievance Officer regarding any act or omission related to the performance of our obligations or your rights under the Act.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl border border-black/10 flex items-start gap-3">
                    <div className="h-7 w-7 rounded-full bg-black text-white flex items-center justify-center font-bold text-[11px] shrink-0 mt-0.5">
                      14
                    </div>
                    <div>
                      <p className="font-bold text-sm text-black">Right to Nominate (Section 14)</p>
                      <p className="mt-1 text-black/70">
                        You have the right to nominate any other individual who shall, in the event of death or incapacity, exercise your rights as a Data Principal in accordance with the provisions of the DPDP Act.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <p className="text-xs text-black/60 italic">
                    To exercise any of the above rights, send an email request from your registered email address to <a href="mailto:grievance@bugzx.space" className="underline font-bold text-black">grievance@bugzx.space</a>. We will respond within 48 hours and process requests within 30 days.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 7 */}
            <div id="children-data" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  07
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Protection of Children &amp; Minors (Section 9)
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  In compliance with <strong>Section 9 of the DPDP Act, 2023</strong>:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-xs text-black/75">
                  <li>Our website and B2B services are intended solely for individuals aged <strong>18 years or older</strong>.</li>
                  <li>We do not knowingly collect, process, or solicit personal data from children under the age of 18 without verifiable consent from the lawful parent or guardian.</li>
                  <li>We strictly do not engage in tracking or behavioral monitoring of children, nor do we serve targeted advertisements directed at minors.</li>
                  <li>If you believe a child has provided us with personal data without parental consent, contact <a href="mailto:grievance@bugzx.space" className="underline font-bold">grievance@bugzx.space</a> and we will delete the record immediately.</li>
                </ul>
              </div>
            </div>

            {/* Section 8 */}
            <div id="third-parties" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  08
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Authorized Data Processors &amp; Cross-Border Transfers (Section 16)
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  Under <strong>Section 8(2) &amp; Section 16 of the DPDP Act, 2023</strong>, BUGZ X only engages verified, enterprise-grade Data Processors under strict Data Processing Agreements (DPAs) that mandate strict security standards.
                </p>

                <div className="space-y-2 text-xs">
                  <div className="p-3.5 rounded-2xl bg-black/[0.02] border border-black/10 flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <div>
                      <p className="font-bold text-black">Supabase Cloud (Database &amp; Secure Storage)</p>
                      <p className="text-black/60">Stores encrypted inquiry records and uploaded CV/document attachments with Row-Level Security (RLS).</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-full bg-black/5 text-[10px] font-bold text-black/70 shrink-0">Encrypted AES-256</span>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-black/[0.02] border border-black/10 flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <div>
                      <p className="font-bold text-black">Brevo / Transactional SMTP Relays</p>
                      <p className="text-black/60">Processes automated receipt confirmations and notification dispatches to clients and internal leads.</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-full bg-black/5 text-[10px] font-bold text-black/70 shrink-0">TLS Encrypted</span>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-black/[0.02] border border-black/10 flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <div>
                      <p className="font-bold text-black">Cloud Hosting Infrastructure (Vercel / Render / Firebase)</p>
                      <p className="text-black/60">Provides distributed content delivery, edge security, DDoS mitigation, and HTTPS SSL certificate termination.</p>
                    </div>
                    <span className="px-2.5 py-1 rounded-full bg-black/5 text-[10px] font-bold text-black/70 shrink-0">SOC2 / ISO 27001</span>
                  </div>
                </div>

                <p className="text-xs text-black/70">
                  Cross-border data transfers adhere to notifications and restrictions issued by the Central Government of India under Section 16 of the DPDP Act. We never sell, rent, or trade your personal data to third-party data brokers.
                </p>
              </div>
            </div>

            {/* Section 9 */}
            <div id="cookies" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  09
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Cookies &amp; Tracking Technologies
                </h2>
              </div>
              <div className="mt-5 space-y-4">
                <p>
                  Cookies are small text files placed on your device to enhance navigation, remember your preferences, and maintain session security.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="p-4 rounded-2xl border border-black/10">
                    <p className="font-bold text-black">1. Strictly Necessary Cookies</p>
                    <p className="mt-1 text-black/70">
                      Essential for the website to function (e.g., security tokens, CSRF protection, routing). These cannot be disabled.
                    </p>
                  </div>

                  <div className="p-4 rounded-2xl border border-black/10">
                    <p className="font-bold text-black">2. Consent &amp; Preference Cookies</p>
                    <p className="mt-1 text-black/70">
                      Stores your consent decisions (`bugzx_dpdp_consent`) so you do not get prompted repeatedly.
                    </p>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    onClick={openCookiePreferences}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black text-white text-xs font-bold tracking-wider hover:bg-black/85 transition-colors"
                  >
                    <Lock className="h-3.5 w-3.5" /> RECONFIGURE COOKIE PREFERENCES
                  </button>
                </div>
              </div>
            </div>

            {/* Section 10 */}
            <div id="retention" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border border-black/12 bg-white">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  10
                </span>
                <h2 className="font-orbitron font-bold text-xl md:text-2xl text-black">
                  Data Retention &amp; Erasure Schedule
                </h2>
              </div>
              <div className="mt-5 space-y-4 text-xs text-black/80">
                <p>
                  Personal data is retained only for the duration necessary to satisfy the purpose for which it was gathered, or as mandated by legal, financial, or tax compliance statutes:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>General Business &amp; Service Inquiries:</strong> Retained for 12 months after the conclusion of communication unless a commercial engagement ensues.</li>
                  <li><strong>Client Contractual &amp; Billing Data:</strong> Retained for up to 7 years in compliance with Indian taxation and corporate accounting standards.</li>
                  <li><strong>Internship &amp; Job Applications:</strong> Retained for 6 months for evaluation and candidate pipeline updates, unless earlier erasure is requested by the candidate.</li>
                  <li><strong>Security Logs &amp; Access Records:</strong> Retained for 180 days in compliance with Indian CERT-In guidelines.</li>
                </ul>
              </div>
            </div>

            {/* Section 11 - Grievance Officer */}
            <div id="grievance" className="scroll-mt-28 p-6 md:p-8 rounded-3xl border-2 border-black bg-white shadow-lg">
              <div className="flex items-center gap-3 text-black">
                <span className="h-8 w-8 rounded-xl bg-black text-white flex items-center justify-center font-bold text-xs">
                  11
                </span>
                <h2 className="font-orbitron font-black text-xl md:text-2xl text-black">
                  Grievance Redressal Officer &amp; Board Escalation (Section 13)
                </h2>
              </div>
              
              <div className="mt-5 space-y-4">
                <p className="text-xs text-black/80 leading-relaxed">
                  In compliance with <strong>Section 13 of the DPDP Act, 2023</strong> and Rule 3(2) of the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, BUGZ X has designated a Grievance Officer to address questions, requests, or disputes concerning data privacy:
                </p>

                <div className="p-5 rounded-2xl bg-black text-white space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] tracking-[0.25em] text-white/50 font-bold uppercase">Designated Grievance Officer</p>
                      <p className="font-orbitron font-bold text-lg mt-0.5">Vishal</p>
                      <p className="text-xs text-white/70">Data Protection &amp; Legal Compliance Lead, BUGZ X</p>
                    </div>
                    <ShieldCheck className="h-8 w-8 text-emerald-400" />
                  </div>

                  <div className="pt-3 border-t border-white/15 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-white/50 text-[10px] uppercase font-semibold">Grievance Email</p>
                      <a href="mailto:grievance@bugzx.space" className="text-white font-bold underline">grievance@bugzx.space</a>
                    </div>
                    <div>
                      <p className="text-white/50 text-[10px] uppercase font-semibold">Official Corporate Desk</p>
                      <a href="mailto:team@bugzx.space" className="text-white font-bold underline">team@bugzx.space</a>
                    </div>
                    <div>
                      <p className="text-white/50 text-[10px] uppercase font-semibold">Acknowledgment SLA</p>
                      <p className="text-emerald-300 font-bold">Within 48 Working Hours</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-[10px] uppercase font-semibold">Resolution SLA</p>
                      <p className="text-emerald-300 font-bold">Within 30 Calendar Days</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-black/[0.03] border border-black/10 text-xs text-black/75 space-y-2">
                  <p className="font-bold text-black flex items-center gap-1.5">
                    <Scale className="h-4 w-4" />
                    Escalation to the Data Protection Board of India (DPBI):
                  </p>
                  <p>
                    If you are unsatisfied with the resolution provided by our Grievance Officer, or if no resolution is offered within the statutory 30-day timeframe, you have the right under the DPDP Act, 2023 to file a formal complaint before the <strong>Data Protection Board of India</strong> in the manner prescribed under the DPDP Rules.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </main>
  );
}
